import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    TextField,
    Typography,
    IconButton,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    Chip,
    InputAdornment,
    Divider,
} from '@material-ui/core';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    Schedule as ScheduleIcon,
    Language as LanguageIcon,
    Category as CategoryIcon,
    AttachFile as AttachFileIcon,
    Description as DescriptionIcon,
    Image as ImageIcon,
    Videocam as VideocamIcon,
    Delete as DeleteIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            maxWidth: '900px',
            width: '90%',
            maxHeight: '85vh',
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dialogContent: {
        padding: theme.spacing(3),
        backgroundColor: '#ffffff',
    },
    searchBox: {
        marginBottom: theme.spacing(3),
        backgroundColor: '#ffffff',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1),
        border: '1px solid #e0e0e0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    templateCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '1px solid #e8e8e8',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
            borderColor: '#1976d2',
        },
    },
    templatePreview: {
        backgroundColor: '#f8f9fa',
        padding: theme.spacing(2.5),
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(2),
        minHeight: '120px',
        fontSize: '0.875rem',
        lineHeight: 1.8,
        whiteSpace: 'pre-wrap',
        color: '#2c3e50',
        border: '1px solid #e0e0e0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    templateHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(1.5),
    },
    templateName: {
        fontWeight: 600,
        fontSize: '1.1rem',
        color: '#1a1a1a',
        marginBottom: theme.spacing(0.5),
    },
    templateMeta: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginTop: theme.spacing(1.5),
    },
    chip: {
        fontSize: '0.75rem',
        height: '24px',
    },
    lastUpdated: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    cardActions: {
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#fafafa',
        borderTop: '1px solid #f0f0f0',
    },
    selectButton: {
        width: '100%',
        textTransform: 'none',
        fontWeight: 600,
        backgroundColor: '#1976d2',
        color: '#fff',
        padding: '10px 16px',
        '&:hover': {
            backgroundColor: '#1565c0',
        },
    },
    detailsContainer: {
        backgroundColor: '#ffffff',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(3),
        border: '1px solid #e8e8e8',
    },
    variableSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2.5),
        backgroundColor: '#f8f9fa',
        borderRadius: theme.spacing(1),
        border: '1px solid #e8e8e8',
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    backButton: {
        marginTop: theme.spacing(2),
    },
    sendButton: {
        marginTop: theme.spacing(2),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        color: theme.palette.text.secondary,
    },
    statusChip: {
        fontWeight: 600,
        fontSize: '0.7rem',
    },
    mediaUploadSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2.5),
        backgroundColor: '#f8f9fa',
        borderRadius: theme.spacing(1),
        border: '1px solid #e8e8e8',
    },
    mediaUploadBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        border: '2px dashed #bdbdbd',
        borderRadius: theme.spacing(1),
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: '#fff',
        '&:hover': {
            borderColor: '#1976d2',
            backgroundColor: '#f5f9ff',
        },
    },
    mediaFileInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#fff',
        borderRadius: theme.spacing(1),
        border: '1px solid #e0e0e0',
    },
    mediaFileDetails: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        overflow: 'hidden',
    },
    mediaFileName: {
        fontSize: '0.875rem',
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '300px',
    },
    mediaFileSize: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
}));

const TemplateModal = ({ open, handleClose, templates, onSelectTemplate }) => {
    const classes = useStyles();
    const [search, setSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [variables, setVariables] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const [renderedContent, setRenderedContent] = useState('');
    const [headerMediaFile, setHeaderMediaFile] = useState(null);
    const [headerMediaFormat, setHeaderMediaFormat] = useState(null);

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
    };

    const extractVariablesByComponent = (components) => {
        const regex = /{{(\d+)}}/g;
        let variables = {
            header: [],
            body: [],
            footer: [],
            buttons: []
        };

        components.forEach((component) => {
            let match;
            const type = component.type?.toLowerCase();
            const text = component.text || '';

            // HEADER com IMAGE/VIDEO/DOCUMENT não precisa de input manual
            // O backend vai usar a URL do template automaticamente
            // Para os botões, verifique a URL para variáveis
            if (type === 'buttons') {
                const buttons = JSON.parse(component.buttons)
                buttons.forEach((button, index) => {
                    if (button.example) {
                        variables[type].push({ type: button.type, prompt: button.example, index: index });
                    }
                    else if (variables[type]) {
                        while ((match = regex.exec(text)) !== null) {
                            variables[type].push(match[0]);
                        }
                    }
                });
            } else if (variables[type]) {
                while ((match = regex.exec(text)) !== null) {
                    variables[type].push({ type: 'text', prompt: match[0] });
                }
            } else {
                console.warn(`Tipo de componente desconhecido: ${type}`);
            }
        });
        return variables;
    };



    const handleSelectTemplate = (template) => {
        const components = template?.components || [];
        const extractedVariables = extractVariablesByComponent(components);

        // Detectar se o template tem HEADER com mídia (IMAGE/VIDEO/DOCUMENT)
        const headerComp = components.find(c => c.type === 'HEADER');
        if (headerComp && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComp.format)) {
            setHeaderMediaFormat(headerComp.format);
        } else {
            setHeaderMediaFormat(null);
        }
        setHeaderMediaFile(null);

        setSelectedTemplate(template);
        setVariables(extractedVariables);
        setRenderedContent(components.map((component) => component?.text).join(`\n`));
    };

    const generateBodyToSave = (content, variables) => {
        let bodyToSave = content;
        Object.keys(variables).forEach((componentType) => {
            const componentVariables = variables[componentType];

            if (Array.isArray(componentVariables)) {
                // Se for um array, iteramos sobre ele substituindo as variáveis
                componentVariables.forEach((variable, index) => {
                    const placeholder = `{{${index + 1}}}`;
                    const value = variable?.value || '';
                    bodyToSave = bodyToSave.replace(placeholder, value);
                });
            } else if (typeof componentVariables === 'object') {
                // Se for um objeto (caso do header com uma imagem ou outro tipo de conteúdo)
                if (componentType === 'header') {
                    // Substituir um placeholder específico no header por uma URL de imagem
                    Object.keys(componentVariables).forEach((key, index) => {
                        const value = componentVariables[key]?.value || '';
                        if (value.startsWith("http")) {                            
                            bodyToSave = `${value} \n ${bodyToSave}`
                        } else {
                            const placeholder = `{{${index + 1}}}`;

                            bodyToSave = bodyToSave.replace(placeholder, value);
                        }
                    });
                } else {
                    // Para texto ou outros tipos de componentes, substituímos as variáveis como antes
                    Object.keys(componentVariables).forEach((key, index) => {
                        const placeholder = `{{${index + 1}}}`;
                        const value = componentVariables[key]?.value || '';
                        bodyToSave = bodyToSave.replace(placeholder, value);
                    });
                }
            } else {
                console.error(`Expected array or object for componentType: ${componentType}, but got`, componentVariables);
            }
        });
        return bodyToSave;
    };

    const handleSendTemplate = () => {
        const bodyToSave = generateBodyToSave(renderedContent, variableValues);
        const templateWithVariables = {
            ...selectedTemplate,
            variables: variableValues,
            bodyToSave,
            headerMediaFile: headerMediaFile || null,
        };
        onSelectTemplate(templateWithVariables);
    };

    const handleVariableChange = (componentType, index, value, buttonIndex) => {
        // Atualiza o estado das variáveis por tipo de componente
        const newComponentValues = { ...variableValues[componentType], [index]: { value, buttonIndex } };
        const newValues = { ...variableValues, [componentType]: newComponentValues };
        setVariableValues(newValues);
    };

    const filteredTemplates = templates.filter((template) =>
        template?.shortcode?.toLowerCase().includes(search?.toLowerCase())
    );

    const getAcceptType = (format) => {
        switch (format) {
            case 'DOCUMENT': return '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
            case 'IMAGE': return 'image/jpeg,image/png';
            case 'VIDEO': return 'video/mp4,video/3gpp';
            default: return '*/*';
        }
    };

    const getMediaIcon = (format) => {
        switch (format) {
            case 'DOCUMENT': return <DescriptionIcon style={{ fontSize: '2rem', color: '#1976d2' }} />;
            case 'IMAGE': return <ImageIcon style={{ fontSize: '2rem', color: '#1976d2' }} />;
            case 'VIDEO': return <VideocamIcon style={{ fontSize: '2rem', color: '#1976d2' }} />;
            default: return <AttachFileIcon style={{ fontSize: '2rem', color: '#1976d2' }} />;
        }
    };

    const getMediaLabel = (format) => {
        switch (format) {
            case 'DOCUMENT': return 'documento (PDF, DOC, XLS...)';
            case 'IMAGE': return 'imagem (JPG, PNG)';
            case 'VIDEO': return 'vídeo (MP4, 3GPP)';
            default: return 'arquivo';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const handleMediaFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setHeaderMediaFile(file);
        }
        e.target.value = '';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Data não disponível';
        try {
            const date = parseISO(dateString);
            return format(date, "dd 'de' MMM yyyy", { locale: ptBR });
        } catch {
            return 'Data inválida';
        }
    };

    const getTemplatePreview = (components) => {
        if (!components || components.length === 0) return 'Sem preview disponível';
        return components
            .filter(comp => comp?.text)
            .map(comp => comp.text)
            .join('\n\n');
    };

    const getCategoryColor = (category) => {
        const colors = {
            'UTILITY': 'primary',
            'MARKETING': 'secondary',
            'AUTHENTICATION': 'default',
        };
        return colors[category?.toUpperCase()] || 'default';
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            className={classes.dialog}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Templates do WhatsApp
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {!selectedTemplate ? (
                    <>
                        <Box className={classes.searchBox}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Pesquisar templates..."
                                value={search}
                                onChange={handleSearchChange}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        {filteredTemplates.length === 0 ? (
                            <Box className={classes.emptyState}>
                                <Typography variant="h6" gutterBottom>
                                    Nenhum template encontrado
                                </Typography>
                                <Typography variant="body2">
                                    Tente pesquisar com outros termos
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {filteredTemplates.map((template, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <Card className={classes.templateCard}>
                                            <CardContent>
                                                <Box className={classes.templateHeader}>
                                                    <Typography className={classes.templateName}>
                                                        {template.shortcode}
                                                    </Typography>
                                                    <Chip
                                                        label={template.status || 'APPROVED'}
                                                        size="small"
                                                        color="primary"
                                                        className={classes.statusChip}
                                                    />
                                                </Box>

                                                <Box className={classes.templatePreview}>
                                                    {getTemplatePreview(template.components)}
                                                </Box>

                                                <Box className={classes.templateMeta}>
                                                    <Chip
                                                        icon={<CategoryIcon style={{ fontSize: '0.9rem' }} />}
                                                        label={template.category || 'UTILITY'}
                                                        size="small"
                                                        color={getCategoryColor(template.category)}
                                                        className={classes.chip}
                                                    />
                                                    <Chip
                                                        icon={<LanguageIcon style={{ fontSize: '0.9rem' }} />}
                                                        label={template.language || 'pt_BR'}
                                                        size="small"
                                                        variant="outlined"
                                                        className={classes.chip}
                                                    />
                                                </Box>

                                                {template.updatedAt && (
                                                    <Box className={classes.lastUpdated}>
                                                        <ScheduleIcon style={{ fontSize: '0.9rem' }} />
                                                        Last Updated: {formatDate(template.updatedAt)}
                                                    </Box>
                                                )}
                                            </CardContent>

                                            <CardActions className={classes.cardActions}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className={classes.selectButton}
                                                    onClick={() => handleSelectTemplate(template)}
                                                    startIcon={<SendIcon />}
                                                >
                                                    Selecionar Template
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                ) : (
                    <Box className={classes.detailsContainer}>
                        <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
                            {selectedTemplate.shortcode}
                        </Typography>
                        
                        <Divider style={{ margin: '16px 0' }} />

                        <Box className={classes.templatePreview} style={{ minHeight: 'auto' }}>
                            {renderedContent}
                        </Box>

                        <Box className={classes.templateMeta} style={{ marginTop: 16 }}>
                            <Chip
                                icon={<CategoryIcon />}
                                label={selectedTemplate.category || 'UTILITY'}
                                color={getCategoryColor(selectedTemplate.category)}
                            />
                            <Chip
                                icon={<LanguageIcon />}
                                label={selectedTemplate.language || 'pt_BR'}
                                variant="outlined"
                            />
                        </Box>

                        {/* Upload de mídia para HEADER (DOCUMENT/IMAGE/VIDEO) */}
                        {headerMediaFormat && (
                            <Box className={classes.mediaUploadSection}>
                                <Typography className={classes.sectionTitle}>
                                    <AttachFileIcon /> HEADER — Anexar {getMediaLabel(headerMediaFormat)}
                                </Typography>
                                {!headerMediaFile ? (
                                    <label>
                                        <input
                                            type="file"
                                            accept={getAcceptType(headerMediaFormat)}
                                            onChange={handleMediaFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        <Box className={classes.mediaUploadBox}>
                                            {getMediaIcon(headerMediaFormat)}
                                            <Typography variant="body2" style={{ marginTop: 8, color: '#666' }}>
                                                Clique para selecionar um {getMediaLabel(headerMediaFormat)}
                                            </Typography>
                                        </Box>
                                    </label>
                                ) : (
                                    <Box className={classes.mediaFileInfo}>
                                        <Box className={classes.mediaFileDetails}>
                                            {getMediaIcon(headerMediaFormat)}
                                            <Box>
                                                <Typography className={classes.mediaFileName}>
                                                    {headerMediaFile.name}
                                                </Typography>
                                                <Typography className={classes.mediaFileSize}>
                                                    {formatFileSize(headerMediaFile.size)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton size="small" onClick={() => setHeaderMediaFile(null)}>
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Renderização dos campos para preencher as variáveis */}
                        {Object.keys(variables).map((componentType, index) => (
                            <div key={`${componentType}-${index}`}>
                                {variables[componentType].length > 0 && (
                                    <Box className={classes.variableSection}>
                                        <Typography className={classes.sectionTitle}>
                                            {componentType.toUpperCase()}
                                        </Typography>
                                        {variables[componentType].map((variable, idx) => (
                                            <TextField
                                                key={`${componentType}-${idx}`}
                                                label={variable?.prompt}
                                                value={variableValues[componentType]?.[idx]?.value || ''}
                                                onChange={(e) => handleVariableChange(componentType, idx, e.target.value, variable?.index || 0)}
                                                fullWidth
                                                variant="outlined"
                                                margin="normal"
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}
                            </div>
                        ))}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendTemplate}
                            fullWidth
                            size="large"
                            className={classes.sendButton}
                            startIcon={<SendIcon />}
                        >
                            Enviar Template
                        </Button>
                        
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSelectedTemplate(null);
                                setVariableValues({});
                                setRenderedContent('');
                                setHeaderMediaFile(null);
                                setHeaderMediaFormat(null);
                            }}
                            fullWidth
                            className={classes.backButton}
                            startIcon={<ArrowBackIcon />}
                        >
                            Voltar para Templates
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TemplateModal;
